import { motion } from "motion/react"
import { BookOpen, FileText, Video, Link, Download } from "lucide-react"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, bounce: 0.3, duration: 0.45 },
  },
}

export function ResourcesTab() {
  return (
    <motion.div
      className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="mb-8" variants={itemVariants}>
        <div className="flex items-center gap-3 mb-3">
          <motion.div
            className="w-12 h-12 rounded-xl bg-[#3B82F6] border-3 border-[#2A2A45] flex items-center justify-center shadow-[0_0_10px_rgba(243,112,32,0.15)]"
            animate={{ rotate: [0, 6, -6, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <BookOpen className="w-6 h-6 text-white" />
          </motion.div>
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-white">Resources</h1>
            <p className="text-[#9A9ABF] font-medium">
              Everything your agency needs in one place.
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-6">
        {[
          {
            title: "Sales Playbooks",
            description: "Battle-tested scripts and objection handling guides for common scenarios.",
            icon: FileText,
            color: "#3B82F6",
          },
          {
            title: "Training Videos",
            description: "Short walkthroughs for onboarding, deal submissions, and team management.",
            icon: Video,
            color: "#22C55E",
          },
          {
            title: "Useful Links",
            description: "Quick access to partner portals, forms, and compliance resources.",
            icon: Link,
            color: "#F59E0B",
          },
        ].map((card) => (
          <motion.div
            key={card.title}
            className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-5 shadow-[6px_6px_0_0_#1a1a1a]"
            variants={itemVariants}
            whileHover={{ y: -3, scale: 1.01, boxShadow: "8px 8px 0 0 #1a1a1a" }}
          >
            <div
              className="w-11 h-11 rounded-xl border-3 border-[#2A2A45] flex items-center justify-center mb-3"
              style={{ backgroundColor: card.color }}
            >
              <card.icon className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-black text-white">{card.title}</h3>
            <p className="text-sm text-[#9A9ABF] mt-2">{card.description}</p>
          </motion.div>
        ))}
      </div>

      {/* Oval Handbooks */}
      <motion.div variants={itemVariants}>
        <h2
          className="text-xl font-black text-white mb-3"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          Oval Handbooks
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Oval Handbook Part 1",
              url: "https://cdn.ovalstorage.com/resources/Oval%20Handbook%20Part%20-%201.pdf",
            },
            {
              title: "Oval Handbook Part 2",
              url: "https://cdn.ovalstorage.com/resources/Oval%20Handbook%20Part%20-%202.pdf",
            },
          ].map((handbook) => (
            <motion.a
              key={handbook.title}
              href={handbook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-4 shadow-[0_0_10px_rgba(243,112,32,0.15)] cursor-pointer"
              whileHover={{ y: -2, scale: 1.01, boxShadow: "6px 6px 0 0 #1a1a1a" }}
              whileTap={{ scale: 0.98, boxShadow: "2px 2px 0 0 #1a1a1a" }}
            >
              <div className="w-12 h-12 rounded-xl bg-red-500 border-3 border-[#2A2A45] flex items-center justify-center shrink-0 shadow-[0_0_6px_rgba(243,112,32,0.1)]">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className="font-bold text-white text-sm"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  {handbook.title}
                </h3>
                <p
                  className="text-xs text-[#6A6A8A] font-medium mt-0.5"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  PDF Document
                </p>
              </div>
              <Download className="w-5 h-5 text-[#6A6A8A] shrink-0" />
            </motion.a>
          ))}
        </div>
      </motion.div>
    </motion.div>
  )
}
